import { logNotification, cloneEvent, User, App, Session, Release } from '@microsoft/oteljs';
import { addOneDSFields, preprocessEvent, areCoreFieldsValid, CUSTOMER_CONTENT_TENANT_TOKEN, getTimestamp } from './InternalUtils';
import { newGuid } from '@microsoft/1ds-core-js';
import { OneDSEndpoint } from './DataModels';
var MAX_CUSTOMER_CONTENT_MAJOR_VERSION = 2;
/**
 * Get the corresponding 1DS CCCO Endpoint for a customer content event, for sending via an external instance of the 1DS SDK
 * @param oneDSEndpoint - The public 1DS Endpoint.
 */
export function getCorrespondingCCCOEndpoint(oneDSEndpoint) {
    switch (oneDSEndpoint) {
        case OneDSEndpoint.PUBLIC:
            return "https://office-c.events.data.microsoft.com/OneCollector/1.0/" /* OneDSCCCOEndpoint.PUBLIC */;
        case OneDSEndpoint.EUDB:
            return "https://eu-office-c.events.data.microsoft.com/OneCollector/1.0/" /* OneDSCCCOEndpoint.EUDB */;
        default:
            return undefined;
    }
}
/**
 * OneDSHelper helps to create a 1DS telemetry event
 *
 */
var OneDSHelper = /** @class */ (function () {
    /**
     * Constructs an 1DS Helper
     * @param persistentDataFields - Data fields to be sent with every event; these data fields are assumed to
     * be top-level static fields, and will not be prepended with "Data."
     * @param oneDSHelperProperties - Properties on the sinks
     */
    function OneDSHelper(persistentDataFields, oneDSHelperProperties) {
        var _this = this;
        /**
         * Number of telemetry events processed
         * Also equal to the Event Sequence
         */
        this.eventsProcessed = 0;
        /**
         * Number of telemetry events sent
         */
        this.eventsSent = 0;
        /**
         * Number of telemetry events discarded by the 1DS SDK
         */
        this.eventsDiscarded = 0;
        // #region Private variables
        var _preprocessors = [];
        var _optionalEventsEnabled = false;
        var _isEnabled = true;
        var _diagnosticLevelChecker = {
            name: 'DiagnosticLevel',
            /**
             * Determine whether an event can be sent. Generally returns true; returns
             * false if the event does not have the appropriate event flags and the optional events override is not enabled.
             * @param telemetryEvent - An event to process
             */
            processEvent: function (telemetryEvent) {
                var diagnosticLevel = telemetryEvent.eventFlags.diagnosticLevel;
                return (_optionalEventsEnabled ||
                    diagnosticLevel === 10 /* DiagnosticLevel.Required */ ||
                    diagnosticLevel === 110 /* DiagnosticLevel.RequiredServiceData */ ||
                    diagnosticLevel === 120 /* DiagnosticLevel.RequiredServiceDataForEssentialServices */);
            }
        };
        // These are persistent data fields kept by the OneDSHelper
        var _helperPersistentOneDSDataFields = {};
        var _oneDSSinkSessionId = newGuid();
        // let _epoch = new Date().getTime().toString();
        // #endregion
        // #region Public functions
        this.init = function (_disableSinkOnInvalidCoreFields) {
            return areCoreFieldsValid(_helperPersistentOneDSDataFields, false);
        };
        this.begin = function () {
            _isEnabled = _isEnabled && areCoreFieldsValid(_helperPersistentOneDSDataFields, true);
            return _isEnabled;
        };
        this.getOneDSTelemetryEvent = function (telemetryEvent) {
            return getOneDSEventInternal(telemetryEvent, {
                eventType: 1 /* EventType.SendTelemetryEvent */
            });
        };
        this.getOneDSCustomerContent = function (telemetryEvent) {
            var customerContentVersion = telemetryEvent.telemetryProperties.customerContentVersion;
            if (customerContentVersion &&
                Math.floor(customerContentVersion) <= MAX_CUSTOMER_CONTENT_MAJOR_VERSION &&
                telemetryEvent.telemetryProperties.customerContentType === 1 /* CustomerContentType.MocaAddIn */) {
                return getOneDSEventInternal(telemetryEvent, { eventType: 2 /* EventType.CustomerContent */ });
            }
            return undefined;
        };
        /**
         * Gets a tenant token to use for sending the event
         * @param telemetryEvent - The event to be sent, which should have a tenant token in its properties
         */
        var getTenantToken = function (telemetryEvent, eventType) {
            if (eventType === 2 /* EventType.CustomerContent */) {
                return CUSTOMER_CONTENT_TENANT_TOKEN;
            }
            if (!telemetryEvent.telemetryProperties ||
                (eventType === 1 /* EventType.SendTelemetryEvent */ && !telemetryEvent.telemetryProperties.ariaTenantToken)) {
                logNotification(0 /* LogLevel.Error */, 1 /* Category.Sink */, function () { return 'Missing Aria Token'; });
                return undefined;
            }
            if (eventType === 1 /* EventType.SendTelemetryEvent */) {
                return telemetryEvent.telemetryProperties.ariaTenantToken;
            }
            return undefined;
        };
        /**
         * Create a 1DS telemetry event with the Event contract and data fields
         * @param telemetryEvent - The telemetry event to be sent
         * @param eventProperties - Properties for the event
         */
        var getOneDSEventInternal = function (telemetryEvent, eventProperties) {
            if (!_isEnabled) {
                return;
            }
            // Clone the event, because preprocessEvent might modify it
            var internalEvent = cloneEvent(telemetryEvent);
            if (preprocessEvent(internalEvent, _preprocessors)) {
                var eventTime = getTimestamp(internalEvent).toISOString();
                var data = {
                    'Event.Name': internalEvent.eventName,
                    'Event.Source': 'OTelJS',
                    'Event.Time': { value: eventTime, propertyType: 9 /* EventPropertyType.DateTime */ }
                };
                _this.eventsProcessed++;
                data['Event.Sequence'] = { value: _this.eventsProcessed, propertyType: 4 /* EventPropertyType.Int64 */ };
                data['Event.Id'] = _oneDSSinkSessionId + '.' + _this.eventsProcessed;
                for (var fieldName in _helperPersistentOneDSDataFields) {
                    data[fieldName] = _helperPersistentOneDSDataFields[fieldName];
                }
                // If a field is found that makes the event invalid, drop the event
                if (!addOneDSFields(data, internalEvent.dataFields, /*prependDataToken*/ true, eventProperties.eventType)) {
                    logNotification(0 /* LogLevel.Error */, 1 /* Category.Sink */, function () { return 'Dropping Event: ' + internalEvent.eventName; });
                    return undefined;
                }
                var baseType = 'custom';
                // Event Contract
                if (internalEvent.eventContract) {
                    if (internalEvent.eventContract.name) {
                        data['Event.Contract'] = internalEvent.eventContract.name;
                        baseType += '.' + internalEvent.eventContract.name.toLowerCase().replace(/\./g, '_');
                    }
                    addOneDSFields(data, internalEvent.eventContract.dataFields, /*prependDataToken*/ false, eventProperties.eventType);
                }
                var token = getTenantToken(internalEvent, eventProperties.eventType);
                if (!token) {
                    return undefined;
                }
                var oneDSEvent = {
                    iKey: token,
                    name: internalEvent.eventName,
                    data: data,
                    time: eventTime,
                    baseType: baseType,
                    ext: {
                        sdk: {
                            seq: _this.eventsProcessed
                        }
                    }
                };
                return oneDSEvent;
            }
            return undefined;
        };
        this.addPreprocessor = function (preprocessor) {
            _preprocessors.push(preprocessor);
        };
        this.getOneDSPersistentDataFields = function () {
            return _helperPersistentOneDSDataFields;
        };
        this.getPreprocessors = function () {
            return _preprocessors;
        };
        // Persistent data fields should not contain customer content
        // or direct numeric data
        // We use this later in the constructor, so saving it as a temporary variable to save bytes
        var addPersistentDataFields = function (dataFields) {
            addOneDSFields(_helperPersistentOneDSDataFields, dataFields, /*prependDataToken*/ false, 1 /* EventType.SendTelemetryEvent */);
        };
        this.addPersistentDataFields = addPersistentDataFields;
        this.setOptionalEventsEnabled = function (enabled) {
            _optionalEventsEnabled = enabled;
        };
        this.setFullEventsEnabled = this.setOptionalEventsEnabled;
        // #endregion
        // #region Actual constructor stuff
        if (oneDSHelperProperties.enableOptionalEvents) {
            _optionalEventsEnabled = true;
        }
        addPersistentDataFields(persistentDataFields);
        var coreFields = oneDSHelperProperties.coreFields;
        if (coreFields) {
            addPersistentDataFields(App.getFields(coreFields.app));
            addPersistentDataFields(User.getFields(coreFields.user));
            addPersistentDataFields(Session.getFields(coreFields.session));
            var release = coreFields.release;
            if (release) {
                addPersistentDataFields(Release.getFields(release));
            }
        }
        this.addPreprocessor(_diagnosticLevelChecker);
        // #endregion
    }
    return OneDSHelper;
}());
export { OneDSHelper };
//# sourceMappingURL=OneDSHelper.js.map